import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs/index';
import { Store } from '@ngrx/store';
import { ParseKeys } from '@cureskin/api-client';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { WindowRefService } from '@services/window-ref-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { CurrentComponentService } from '@services/current-component';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { BroadcastService } from '@services/broadcast-service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NotificationPermissionSheetComponent } from
  '@shared/bottom-sheet-layouts/notification-permission-sheet/notification-permission-sheet.component';
import { DeliveryDateService } from '@services/delivery-date-service/delivery-date.service';
import { TimeService } from '@services/time-service';
import { AppConfig } from '../../app.config';
import { OrderHelper } from '../../../helper/order-helper';
import { resetCache } from '@services/connection-service/cache-interceptor';
import { ExploreService } from '@services/explore-service/explore.service';
import {UserService} from '@services/user';

@Component({
  selector: 'user-order-success',
  templateUrl: './user-order-success.html',
  standalone: false,
})

export class UserOrderSuccessComponent implements OnInit, OnDestroy {
  showSurpriseGift$: Observable<boolean> = this.store.select(fromSelectors.selectOrderSurpriseGiftClaimPending);
  referralCashValue$: Observable<number> = this.store.select(fromSelectors.selectReferralCashValue);
  showExploreTab: boolean = false;
  private readonly exploreSubscription: Subscription;
  expectedDeliveryDate: { from?: Date, to?: Date } = {};
  deliveryIntervalInDays: number = 2;
  user: any;
  isNotificationPermissionEnabled: any;
  experiments: any;
  isConsulationOrder: boolean = false;
  orderId: string;
  excludeCategory: Array<string> = ['RETENTION'];
  orderDeliveryDateToCurrentDateDifference: number = 0;
  isEnglishOrHindiUser: boolean = false;
  finasterideConsentExp: any;
  shouldShowFinasterideConsent: boolean = false;
  finasterideMargId: string = '';
  userFirstName: string = '';
  allocatedDoctor: any;
  loading: boolean = false;
  acqRebranding2025: boolean = false;
  extraBannersExperiment: boolean = false;
  showWhatsappBanner: boolean = false;
  showFraudBanner: boolean = false;
  showExploreBanner: boolean = false;
  showReferralBanner: boolean = false;
  showCheckupBanner: boolean = false;
  isFraudAccordionOpen: boolean = false;
  shouldShowExploreBannerUI: boolean = false;
  shouldShowReferralBannerUI: boolean = false;
  shouldShowWhatsappBannerUI: boolean = false;
  isRegimenOrder: boolean = false;
  isiOSDevice: boolean = false;
  isAcqUser: boolean = false;
  acquisitionRegimenOrderCheckupUI: boolean = false;
  constructor(public route: ActivatedRoute,
    private windowRef: WindowRefService,
    public eventLogger: EventLoggerService,
    public appBridge: AppWebBridgeService,
    public conn: ConnectionService,
    public appConfig: AppConfig,
    private router: Router,
    public timeService: TimeService,
    private localStorageService: LocalStorageService,
    private currentComponent: CurrentComponentService,
    private commonUtilityHelper: CommonUtilityHelper,
    public broadcast: BroadcastService,
    public store: Store,
    public readonly bottomSheet: MatBottomSheet,
    public deliveryDateService: DeliveryDateService,
    private readonly orderHelper: OrderHelper,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly exploreService: ExploreService,
    private readonly userService: UserService,
    ) {
    resetCache();
    this.currentComponent.set(this);
    // Subscribe to explore visibility changes
    this.exploreSubscription = this.exploreService.showExploreTab$.subscribe(
      (showTab: boolean) => {
        this.showExploreTab = showTab;
        this.updateExtraBannersVisibility();
      }
    );
    // this.commonUtilityHelper.showPaperBombAnimation(1000);
  }

  async ngOnInit(): Promise<void> {
    this.appBridge.setStatusBarColor('cure/accentDark');
    this.loading = true;
    this.isiOSDevice = (await this.appBridge.requestOSInformation()) === 'iOS' || this.windowRef.isSafariBrowser();
    this.user = this.conn.getActingUser();
    this.isAcqUser = this.userService.isUnpaidUser(this.user)
    this.userFirstName = this.user.get('PatientName')?.split(' ')[0];
    if (this.user.get('allocatedDoctor')) {
      this.allocatedDoctor = await this.conn.findUserByObjectId(this.user.get('allocatedDoctor')?.id);
    }
    this.store.dispatch(fromActions.OrderSuccessBegin());
    this.localStorageService.delete('freeProduct');
    this.orderId = this.route.snapshot.queryParams?.orderId;
    this.callGokWickSuccess();
    this.setEventOnProductsOrder();
    this.setEventOnRegimenOrder();
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'order-success' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.experiments = await this.conn.findUserActiveExperiments();
    // Let the service handle the experiment logic
    this.exploreService.handleExploreTabExperiment(
      this.experiments,
      this.user?.get('orderState')
    );
    this.isEnglishOrHindiUser = [this.appConfig.Shared.Languages.EN, this.appConfig.Shared.Languages.HI].includes(this.user.get('languagePreference'));
    this.experiments?.forEach((experiment: any): void => {
      if (experiment.key === 'finasteride_order_success_extra_info') {
        this.finasterideConsentExp = experiment;
        this.finasterideMargId = experiment.variant.margId;
      }
      if (experiment.key === 'acq_rebranding_2025') {
        this.acqRebranding2025 = true;
      }
      if (experiment.key === 'order_page_extra_banners') {
        this.extraBannersExperiment = true;
        const variant = experiment.variant || {};
        this.showWhatsappBanner = !!variant?.whatsapp;
        this.showFraudBanner = !!variant?.fraud;
        this.showExploreBanner = !!variant?.explore;
        this.showReferralBanner = !!variant?.referral;
        this.showCheckupBanner = !!variant?.checkup;
      }
    });
    this.updateExtraBannersVisibility();
    this.shouldShowFinasterideConsent = await this.showFinasterideConsent();
    this.isConsulationOrder = this.route.snapshot.queryParams?.orderType === this.appConfig.Shared.Order.Type.CONSULTATION;
    this.isRegimenOrder = this.route.snapshot.queryParams?.orderType === this.appConfig.Shared.Order.Type.REGIMEN;
    this.acquisitionRegimenOrderCheckupUI = this.isAcqUser && this.isRegimenOrder && this.showCheckupBanner;
    await this.logPurchaseEvent();
    if (this.orderId) await this.setDeliveryDate();
    this.loading = false;
    const notificationExperiment = this.experiments?.find((item:any):any => item.key === 'notification_permission_popup');
    if (notificationExperiment) {
      setTimeout((): Promise<void> => this.openBottomSheet(), 1000);
    }
  }

  async setDeliveryDate(): Promise<void> {
    // If user language is not english or hindi, then we show the static delivery date
    if (!this.isEnglishOrHindiUser) {
      this.expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(null, this.deliveryIntervalInDays);
      return;
    }
    try {
      const res = await this.conn.getExpectedDeliveryDateByOrderId(this.orderId);
      const responseDateISO = res?.expectedDeliveryDate;
      const todayDate = new Date();
      this.orderDeliveryDateToCurrentDateDifference = this.deliveryDateService.getDeliveryDateDifference(responseDateISO, todayDate);
      this.expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(
        responseDateISO,
        this.deliveryIntervalInDays,
      );
    } catch (error) {
      this.expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(null, this.deliveryIntervalInDays);
    }
  }

  async logPurchaseEvent(): Promise<void> {
    const isFemaleUser = this.user?.get('Gender') === this.appConfig.Shared.Gender.FEMALE;
    const isRegimenOrder = this.route.snapshot.queryParams?.orderType === this.appConfig.Shared.Order.Type.REGIMEN;
    const where: any = { user: this.user };
    const select: Array<ParseKeys<Table.Order>> = ['category'];
    const orders = await this.conn.fetchOrders(where, select, []);
    const order = orders.find((each: any): any => each.objectId === this.orderId);
    const isAcquisitionOrder = !this.excludeCategory.includes(order?.get('category'));
    if (isRegimenOrder && isAcquisitionOrder) {
      this.appBridge.logEventInBranchAndFirebaseFromiOS({
        branch: { name: 'PURCHASE', value: this.route.snapshot.queryParams?.amount },
        firebase: { name: 'purchase', value: this.route.snapshot.queryParams?.amount},
      });
      if (isFemaleUser) {
        this.appBridge.logEventInBranchAndFirebaseFromiOS({
          branch: { name: 'PurchaseW1845', value: this.route.snapshot.queryParams?.amount, },
          firebase: { name: 'PurchaseW1845', value: this.route.snapshot.queryParams?.amount, },
        });
      }
    }
  }

  async openBottomSheet(): Promise<void> {
    // call function to check permission status true or false
    // if true then perimssionn already given
    this.isNotificationPermissionEnabled = await this.appBridge.requestNotificationStatus();
    if (!this.isNotificationPermissionEnabled) {
      this.bottomSheet.open(NotificationPermissionSheetComponent, {
        data: {
          content: 'order-placed',
          callback: (): void => {
            this.openNativePermissionPopup();
          },
        },
      });
    }
  }

  openNativePermissionPopup(): void {
    this.appBridge.requestNotificationPermission();
  }

  navigateTo(url: string): void {
    this.conn.navigateToURL(url, false, true);
  }
  /**
 * To track product orders
 */
  setEventOnProductsOrder(): void {
    if (this.route.snapshot.queryParams?.orderType === this.appConfig.Shared.Order.Type.PRODUCT) {
      this.eventLogger.trackInFirebaseAndBranch('PRODUCT_CLICKED', {
        currency: 'INR',
        value: this.route.snapshot.queryParams?.amount,
      });
    }
  }
  /**
 * To track retention regimen orders
 */
  setEventOnRegimenOrder(): void {
    if (this.route.snapshot.queryParams?.orderType === this.appConfig.Shared.Order.Type.REGIMEN && this.user.isPaid()) {
      this.eventLogger.trackInFirebaseAndBranch('REGIMEN_CLICKED');
    }
  }

  async handleBackPress(): Promise<boolean> {
    // If fraud accordion is open, close it instead of navigating away
    if (this.isFraudAccordionOpen) {
      this.closeFraudAccordion('android_back');
      return true; // Prevent navigation, accordion was closed
    }
    this.conn.navigateToURL('/user?tab=home', false, true);
    return true;
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
    if (this.exploreSubscription) {
      this.exploreSubscription.unsubscribe();
    }
  }

  callGokWickSuccess(): void {
    const { gokWik: gokWikParam }: { gokWik: string } = this.route.snapshot.queryParams as { gokWik: string };
    if (!gokWikParam) {
      return;
    }
    const gokWikJSON = JSON.parse(decodeURIComponent(gokWikParam));
    if (!gokWikJSON.request_id) {
      return;
    }
    this.appBridge.requestGokWikSuccess(gokWikJSON);
  }

  async showFinasterideConsent(): Promise<boolean> {
    const orderType  = this.route.snapshot.queryParams?.orderType;
    if (orderType === undefined || orderType === null || !this.finasterideConsentExp?.enable) return false;
    const isRegimenOrder = this.orderHelper.isOrderTypeRegimen(orderType);
    if (!isRegimenOrder) return false;
    const regimenId = this.route.snapshot.queryParams?.regimenId;
    const orderedRegimen = await this.conn.findRegimenByRegimenId(regimenId, ['products']);
    const regimenProducts = orderedRegimen?.products;
    const doesRegimenContainFinasteride = regimenProducts?.some((product: any): boolean => product.margId === this.finasterideMargId);
    return doesRegimenContainFinasteride;
  }

  showOrderSuccess(): void {
    this.shouldShowFinasterideConsent = false;
    this.changeDetectorRef.detectChanges();
  }

  openFraudAccordion(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: 'fraud_order_successful'
    }));
    this.isFraudAccordionOpen = true;
    this.updateExtraBannersVisibility();
    this.changeDetectorRef.detectChanges();
  }

  closeFraudAccordion(source: string = 'okay_button'): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: 'fraud_accordion_closed',
      source: source
    }));
    this.isFraudAccordionOpen = false;
    this.updateExtraBannersVisibility();
    this.changeDetectorRef.detectChanges();
  }

  openWhatsAppChannel(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'whatsapp_order_successful' }));
    const whatsappUrl = 'https://whatsapp.com/channel/0029Vb65R0VAjPXTZdRR8E1L';
    this.broadcast.broadcast('OPEN_IN_NEW_TAB', { url: whatsappUrl });
  }

  navigateToExplore(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'explore_order_successful' }));
    this.navigateTo('/user?tab=explore');
  }

  navigateToReferral(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'referral_order_successful' }));
    this.navigateTo('/user/wallet?tab=earn&back=home');
  }

  goToCheckupPage(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'checkup_order_successful' }));
    this.router.navigate(['/user/checkup'], { queryParams: { from: 'orderSuccess' } });
  }

  /**
   * Helper to compute banner visibility flags so that template conditions stay simple.
   */
  private updateExtraBannersVisibility(): void {
    if (!this.extraBannersExperiment) {
      this.shouldShowExploreBannerUI = this.showExploreTab && !this.isFraudAccordionOpen;
    } else {
      this.shouldShowExploreBannerUI = this.showExploreBanner && this.showExploreTab && !this.isFraudAccordionOpen;
    }

    if (!this.extraBannersExperiment) {
      this.shouldShowReferralBannerUI = !this.isFraudAccordionOpen;
    } else {
      this.shouldShowReferralBannerUI = this.showReferralBanner && !this.isFraudAccordionOpen;
    }

    // WhatsApp banner should not be shown on iOS devices
    this.shouldShowWhatsappBannerUI = this.showWhatsappBanner && !this.isFraudAccordionOpen && !this.isiOSDevice;
  }
}
